import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FileText, Search, ChevronDown, ChevronUp, Check, X, UserCheck, UserX, Shield, Settings, Plus, Trash2, Clock, Mail, Phone, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { approveAgent, rejectAgent, activateAgent, deactivateAgent, getReportSettings, updateReportSettings, updateAgentTeams } from '../utils/api';
import AgentLayout from '../components/AgentLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminReports = () => {
  const navigate = useNavigate();
  const [agentInfo, setAgentInfo] = useState(null);
  const [agents, setAgents] = useState([]);
  const [reports, setReports] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('agents');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportTypeFilter, setReportTypeFilter] = useState('activity');
  const [expandedReport, setExpandedReport] = useState(null);

  // Report Settings state (per team)
  const [reportSettings, setReportSettings] = useState({});
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsTeamTab, setSettingsTeamTab] = useState('HIG');
  const [editingTeamAgent, setEditingTeamAgent] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('agentToken');
    const info = localStorage.getItem('agentInfo');
    if (!token || !info) { navigate('/agent/login'); return; }
    const parsed = JSON.parse(info);
    if (parsed.role !== 'admin' && parsed.role !== 'super_admin') {
      toast.error('Admin access required');
      navigate('/agent/dashboard');
      return;
    }
    setAgentInfo(parsed);
    loadAdminData(token);
  }, [navigate]);

  const loadAdminData = async (token) => {
    try {
      setIsLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };
      const [agentsRes, reportsRes, quotesRes, contactsRes, appsRes] = await Promise.all([
        axios.get(`${API}/admin/agents`, { headers }).catch(() => ({ data: { agents: [] } })),
        axios.get(`${API}/admin/reports`, { headers }).catch(() => ({ data: { reports: [] } })),
        axios.get(`${API}/quotes`).catch(() => ({ data: { quotes: [] } })),
        axios.get(`${API}/contacts`).catch(() => ({ data: { contacts: [] } })),
        axios.get(`${API}/agent-applications`).catch(() => ({ data: { applications: [] } })),
      ]);
      setAgents(agentsRes.data.agents || []);
      setReports(reportsRes.data.reports || []);
      setQuotes(quotesRes.data.quotes || []);
      setContacts(contactsRes.data.contacts || []);
      setApplications(appsRes.data.applications || []);

      // Load report settings
      try {
        const settingsRes = await getReportSettings(token);
        if (settingsRes.success) setReportSettings(settingsRes.settings);
      } catch (e) {
        console.error('Error loading report settings:', e);
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAgentAction = async (agentId, action) => {
    const token = localStorage.getItem('agentToken');
    try {
      if (action === 'approve') await approveAgent(agentId, token);
      else if (action === 'reject') await rejectAgent(agentId, token);
      else if (action === 'activate') await activateAgent(agentId, token);
      else if (action === 'deactivate') await deactivateAgent(agentId, token);
      toast.success(`Agent ${action}d successfully`);
      loadAdminData(token);
    } catch (error) {
      toast.error(`Failed to ${action} agent`);
    }
  };

  const calcTotals = (acts) => {
    const t = { sales: 0, presentations: 0, annualizedPremium: 0, doorKnocks: 0, phoneCalls: 0 };
    (acts || []).forEach(d => { if (!d.offDay) Object.keys(t).forEach(k => { t[k] += (d[k] || 0); }); });
    return t;
  };

  const pendingAgents = agents.filter(a => a.status === 'pending');
  const filteredReports = reports.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (searchTerm && !r.agentName?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.agentEmail?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (teamFilter !== 'all') {
      const agentTeams = agents.find(a => a.id === r.agentId)?.teams || [];
      if (!agentTeams.includes(teamFilter)) return false;
    }
    if (dateFrom && r.weekStartDate < dateFrom) return false;
    if (dateTo && r.weekStartDate > dateTo) return false;
    return true;
  });

  const tabs = [
    { id: 'agents', label: 'Agents', count: agents.length, badge: pendingAgents.length, icon: Users },
    { id: 'reports', label: 'Activity Reports', count: reports.length, icon: FileText },
    { id: 'quotes', label: 'Quotes', count: quotes.length, icon: FileText },
    { id: 'contacts', label: 'Contacts', count: contacts.length, icon: FileText },
    { id: 'applications', label: 'Applications', count: applications.length, icon: FileText },
    { id: 'settings', label: 'Report Settings', count: null, icon: Settings },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto"></div>
      </div>
    );
  }

  return (
    <AgentLayout title="Admin Dashboard">
      <div data-testid="admin-reports-page">

      <div>
        {/* Pending Agents Alert */}
        {pendingAgents.length > 0 && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-r-lg" data-testid="pending-agents-alert">
            <div className="flex items-center gap-2">
              <Shield className="text-yellow-600" size={20} />
              <p className="font-bold text-yellow-800">{pendingAgents.length} agent(s) pending approval</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md p-2 mb-6 overflow-x-auto" data-testid="admin-tabs">
          <div className="flex gap-2 min-w-max">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSearchTerm(''); setStatusFilter('all'); }}
                className={`flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-md font-semibold transition text-sm relative whitespace-nowrap ${activeTab === tab.id ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                data-testid={`admin-tab-${tab.id}`}>
                <Icon size={16} /> {tab.label} {tab.count !== null ? `(${tab.count})` : ''}
                {tab.badge > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">{tab.badge}</span>
                )}
              </button>
            );
          })}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6" data-testid="admin-filters">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by name or email..." className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-brand-blue focus:outline-none" data-testid="admin-search-input" />
            </div>
            {activeTab === 'reports' && (
              <>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border rounded-md text-sm" data-testid="admin-status-filter">
                  <option value="all">All Status</option>
                  <option value="submitted">Submitted</option>
                  <option value="draft">Draft</option>
                </select>
                <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} className="px-3 py-2 border rounded-md text-sm" data-testid="admin-team-filter">
                  <option value="all">All Teams</option>
                  <option value="HIG">HIG</option>
                  <option value="DOT">DOT</option>
                </select>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500 font-medium">From:</span>
                  <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-2 py-2 border rounded-md text-sm" data-testid="admin-date-from" />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500 font-medium">To:</span>
                  <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-2 py-2 border rounded-md text-sm" data-testid="admin-date-to" />
                </div>
                {(statusFilter !== 'all' || teamFilter !== 'all' || dateFrom || dateTo) && (
                  <button
                    onClick={() => { setStatusFilter('all'); setTeamFilter('all'); setDateFrom(''); setDateTo(''); }}
                    className="px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-md font-medium transition"
                    data-testid="clear-filters-btn"
                  >
                    Clear Filters
                  </button>
                )}
              </>
            )}
          </div>
          {activeTab === 'reports' && filteredReports.length < reports.length && (
            <p className="text-xs text-gray-500 mt-2" data-testid="filter-count">
              Showing {filteredReports.length} of {reports.length} reports
            </p>
          )}
          {activeTab === 'reports' && (
            <div className="mt-2">
              <a
                href={`${API}/admin/reports/export-csv`}
                onClick={(e) => {
                  e.preventDefault();
                  const token = localStorage.getItem('agentToken');
                  fetch(`${API}/admin/reports/export-csv`, { headers: { 'Authorization': `Bearer ${token}` } })
                    .then(r => r.blob())
                    .then(blob => {
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = 'activity_reports.csv'; a.click();
                      URL.revokeObjectURL(url);
                      toast.success('CSV exported');
                    })
                    .catch(() => toast.error('Export failed'));
                }}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-md text-xs font-medium hover:bg-green-700 transition cursor-pointer"
                data-testid="admin-export-csv-btn"
              >
                <Download size={14} /> Export CSV
              </a>
            </div>
          )}
        </div>

        {/* Agents Tab */}
        {activeTab === 'agents' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid="agents-table">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-gray-100"><tr>
                <th className="px-4 py-3 text-left font-bold">Name</th>
                <th className="px-4 py-3 text-left font-bold">Email</th>
                <th className="px-4 py-3 text-left font-bold">Phone</th>
                <th className="px-4 py-3 text-center font-bold">Team</th>
                <th className="px-4 py-3 text-center font-bold">Role</th>
                <th className="px-4 py-3 text-center font-bold">Status</th>
                <th className="px-4 py-3 text-center font-bold">Actions</th>
              </tr></thead>
              <tbody>
                {agents.filter(a => !searchTerm || a.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) || a.email?.toLowerCase().includes(searchTerm.toLowerCase())).map((agent, i) => (
                  <tr key={i} className={`border-b hover:bg-gray-50 ${agent.status === 'pending' ? 'bg-yellow-50' : ''}`} data-testid={`agent-row-${i}`}>
                    <td className="px-4 py-3 font-semibold">{agent.fullName}</td>
                    <td className="px-4 py-3">{agent.email}</td>
                    <td className="px-4 py-3">{agent.phone}</td>
                    <td className="px-4 py-3 text-center">
                      {editingTeamAgent === agent.id ? (
                        <div className="flex flex-col items-center gap-1">
                          <label className="flex items-center gap-1 text-xs">
                            <input type="checkbox" checked={(agent.teams || []).includes('DOT')}
                              onChange={(e) => {
                                const current = agent.teams || [];
                                const updated = e.target.checked ? [...current.filter(t => t !== 'DOT'), 'DOT'] : current.filter(t => t !== 'DOT');
                                setAgents(agents.map(a => a.id === agent.id ? {...a, teams: updated} : a));
                              }} /> DOT
                          </label>
                          <label className="flex items-center gap-1 text-xs">
                            <input type="checkbox" checked={(agent.teams || []).includes('HIG')}
                              onChange={(e) => {
                                const current = agent.teams || [];
                                const updated = e.target.checked ? [...current.filter(t => t !== 'HIG'), 'HIG'] : current.filter(t => t !== 'HIG');
                                setAgents(agents.map(a => a.id === agent.id ? {...a, teams: updated} : a));
                              }} /> HIG
                          </label>
                          <div className="flex gap-1 mt-1">
                            <button onClick={async () => {
                              try {
                                const token = localStorage.getItem('agentToken');
                                await updateAgentTeams(agent.id, agent.teams || [], token);
                                toast.success('Team updated');
                                setEditingTeamAgent(null);
                              } catch (e) { toast.error('Failed to update team'); }
                            }} className="bg-green-500 text-white px-2 py-0.5 rounded text-xs" data-testid={`save-team-${i}`}>
                              <Check size={12} />
                            </button>
                            <button onClick={() => setEditingTeamAgent(null)} className="bg-gray-400 text-white px-2 py-0.5 rounded text-xs">
                              <X size={12} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1 cursor-pointer" onClick={() => setEditingTeamAgent(agent.id)} data-testid={`edit-team-${i}`}>
                          {(agent.teams || []).length > 0 ? (
                            (agent.teams || []).map(t => (
                              <span key={t} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${t === 'DOT' ? 'bg-purple-100 text-purple-800' : 'bg-teal-100 text-teal-800'}`}>{t}</span>
                            ))
                          ) : (
                            <span className="text-gray-400 text-xs">None</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${agent.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>{agent.role}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        agent.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        agent.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        agent.status === 'deactivated' ? 'bg-gray-200 text-gray-600' :
                        'bg-green-100 text-green-800'
                      }`}>{agent.status || 'approved'}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {agent.status === 'pending' && (
                        <div className="flex gap-1 justify-center">
                          <button onClick={() => handleAgentAction(agent.id, 'approve')} className="flex items-center gap-1 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold hover:bg-green-600" data-testid={`approve-agent-${i}`}><UserCheck size={12} /> Approve</button>
                          <button onClick={() => handleAgentAction(agent.id, 'reject')} className="flex items-center gap-1 bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold hover:bg-red-600" data-testid={`reject-agent-${i}`}><UserX size={12} /> Reject</button>
                        </div>
                      )}
                      {(agent.status === 'approved' || !agent.status) && agent.role !== 'admin' && (
                        <button onClick={() => handleAgentAction(agent.id, 'deactivate')} className="bg-gray-400 text-white px-2 py-1 rounded text-xs font-semibold hover:bg-gray-500">Deactivate</button>
                      )}
                      {(agent.status === 'deactivated' || agent.status === 'rejected') && (
                        <button onClick={() => handleAgentAction(agent.id, 'activate')} className="bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold hover:bg-green-600">Activate</button>
                      )}
                    </td>
                  </tr>
                ))}
                {agents.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-400">No agents found</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-3" data-testid="reports-list">
            {filteredReports.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-400">No reports found</div>
            ) : filteredReports.map((report, i) => {
              const totals = calcTotals(report.activities);
              const isExpanded = expandedReport === i;
              return (
                <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden" data-testid={`report-card-${i}`}>
                  <div className="p-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedReport(isExpanded ? null : i)}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div>
                          <p className="font-bold text-brand-blue">{report.agentName}</p>
                          <p className="text-xs text-gray-500">{report.agentEmail}</p>
                        </div>
                        {(() => {
                          const agentTeams = agents.find(a => a.id === report.agentId)?.teams || [];
                          return agentTeams.map(t => (
                            <span key={t} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${t === 'DOT' ? 'bg-purple-100 text-purple-800' : 'bg-teal-100 text-teal-800'}`}>{t}</span>
                          ));
                        })()}
                        <span className="text-sm text-gray-600">{report.weekStartDate} - {report.weekEndDate}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${report.status === 'submitted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{report.status}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex gap-3 text-center">
                          <div><p className="text-lg font-bold text-brand-blue">{totals.sales}</p><p className="text-xs text-gray-500">Sales</p></div>
                          <div><p className="text-lg font-bold text-brand-orange">{totals.presentations}</p><p className="text-xs text-gray-500">Pres.</p></div>
                          <div><p className="text-lg font-bold text-green-600">${totals.annualizedPremium.toFixed(0)}</p><p className="text-xs text-gray-500">Premium</p></div>
                        </div>
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </div>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="border-t px-4 py-3 bg-gray-50 overflow-x-auto">
                      <table className="w-full text-xs min-w-[500px]">
                        <thead><tr className="bg-gray-200">
                          <th className="px-2 py-2 text-left">Day</th><th className="px-2 py-2">Off</th><th className="px-2 py-2">Leads</th><th className="px-2 py-2">Knocks</th><th className="px-2 py-2">Calls</th><th className="px-2 py-2">Pres.</th><th className="px-2 py-2">Sales</th><th className="px-2 py-2">Premium</th>
                        </tr></thead>
                        <tbody>{(report.activities || []).map((d, j) => (
                          <tr key={j} className={`border-b ${d.offDay ? 'opacity-40' : ''}`}>
                            <td className="px-2 py-1 font-semibold">{d.dayOfWeek}</td>
                            <td className="px-2 py-1 text-center">{d.offDay ? 'Yes' : ''}</td>
                            <td className="px-2 py-1 text-center">{d.leadsWorked}</td>
                            <td className="px-2 py-1 text-center">{d.doorKnocks}</td>
                            <td className="px-2 py-1 text-center">{d.phoneCalls}</td>
                            <td className="px-2 py-1 text-center">{d.presentations}</td>
                            <td className="px-2 py-1 text-center font-bold">{d.sales}</td>
                            <td className="px-2 py-1 text-center">${d.annualizedPremium?.toFixed(2)}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Quotes Tab */}
        {activeTab === 'quotes' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid="quotes-table">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-gray-100"><tr>
                <th className="px-4 py-3 text-left font-bold">Name</th><th className="px-4 py-3 text-left font-bold">Email</th><th className="px-4 py-3 text-left font-bold">Phone</th><th className="px-4 py-3 text-left font-bold">Interest</th><th className="px-4 py-3 text-center font-bold">Date</th>
              </tr></thead>
              <tbody>
                {quotes.filter(q => !searchTerm || q.name?.toLowerCase().includes(searchTerm.toLowerCase()) || q.email?.toLowerCase().includes(searchTerm.toLowerCase())).map((q, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">{q.name || `${q.firstName || ''} ${q.lastName || ''}`}</td>
                    <td className="px-4 py-3">{q.email}</td><td className="px-4 py-3">{q.phone}</td>
                    <td className="px-4 py-3">{q.coverageInterest || q.coverage_interest || '-'}</td>
                    <td className="px-4 py-3 text-center text-gray-500">{q.timestamp ? new Date(q.timestamp).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
                {quotes.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-400">No quotes</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid="contacts-table">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-gray-100"><tr>
                <th className="px-4 py-3 text-left font-bold">Name</th><th className="px-4 py-3 text-left font-bold">Email</th><th className="px-4 py-3 text-left font-bold">Subject</th><th className="px-4 py-3 text-left font-bold">Message</th><th className="px-4 py-3 text-center font-bold">Date</th>
              </tr></thead>
              <tbody>
                {contacts.filter(c => !searchTerm || c.name?.toLowerCase().includes(searchTerm.toLowerCase())).map((c, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">{c.name}</td><td className="px-4 py-3">{c.email}</td>
                    <td className="px-4 py-3">{c.subject || '-'}</td><td className="px-4 py-3 max-w-xs truncate">{c.message}</td>
                    <td className="px-4 py-3 text-center text-gray-500">{c.timestamp ? new Date(c.timestamp).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
                {contacts.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-400">No messages</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid="applications-table">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-gray-100"><tr>
                <th className="px-4 py-3 text-left font-bold">Name</th><th className="px-4 py-3 text-left font-bold">Email</th><th className="px-4 py-3 text-left font-bold">Phone</th><th className="px-4 py-3 text-left font-bold">Experience</th><th className="px-4 py-3 text-center font-bold">Date</th>
              </tr></thead>
              <tbody>
                {applications.filter(a => !searchTerm || a.fullName?.toLowerCase().includes(searchTerm.toLowerCase())).map((app, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">{app.fullName || app.name}</td><td className="px-4 py-3">{app.email}</td>
                    <td className="px-4 py-3">{app.phone}</td><td className="px-4 py-3">{app.experience || '-'}</td>
                    <td className="px-4 py-3 text-center text-gray-500">{app.timestamp ? new Date(app.timestamp).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
                {applications.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-400">No applications</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Report Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6" data-testid="report-settings-tab">
            {/* Team Tabs */}
            <div className="flex gap-2">
              {['HIG', 'DOT'].map(team => (
                <button
                  key={team}
                  onClick={() => setSettingsTeamTab(team)}
                  className={`px-5 py-3 rounded-lg font-bold text-sm transition ${
                    settingsTeamTab === team
                      ? (team === 'HIG' ? 'bg-teal-600 text-white' : 'bg-purple-600 text-white')
                      : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-gray-400'
                  }`}
                  data-testid={`settings-team-${team.toLowerCase()}-btn`}
                >
                  {team === 'HIG' ? 'Hawkins Investment Group (HIG)' : 'Disciples of Truth (DOT)'}
                </button>
              ))}
            </div>

            {/* Registration Links */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h4 className="font-semibold text-gray-700 text-sm mb-2">Registration Link for {settingsTeamTab}</h4>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm text-gray-800 select-all" data-testid="team-registration-link">
                  {window.location.origin}/agent/register/{settingsTeamTab.toLowerCase()}
                </code>
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/agent/register/${settingsTeamTab.toLowerCase()}`); toast.success('Link copied!'); }}
                  className="bg-brand-blue text-white px-3 py-2 rounded text-sm font-semibold hover:bg-blue-700">Copy</button>
              </div>
            </div>

            {(() => {
              const teamSettings = reportSettings[settingsTeamTab] || {
                recipients: [{ name: '', email: '', phone: '' }],
                activitySchedule: { dayOfWeek: 0, time: '12:00' },
                goalsSchedule: { dayOfWeek: 0, time: '12:00' },
                emailActive: true,
                smsActive: false,
              };
              const updateTeam = (updates) => {
                setReportSettings({ ...reportSettings, [settingsTeamTab]: { ...teamSettings, ...updates } });
              };
              return (
                <>
                  {/* Delivery Channels */}
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4"><Settings size={20} className="text-brand-blue" /> Delivery Channels</h3>
                    <div className="flex gap-6">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <div className={`relative w-12 h-6 rounded-full transition ${teamSettings.emailActive ? 'bg-green-500' : 'bg-gray-300'}`}
                          onClick={() => updateTeam({ emailActive: !teamSettings.emailActive })}>
                          <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${teamSettings.emailActive ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                        </div>
                        <div>
                          <span className="font-semibold text-sm flex items-center gap-1"><Mail size={14} /> Email</span>
                          <span className="text-xs text-gray-500">{teamSettings.emailActive ? 'Active' : 'Inactive'}</span>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <div className={`relative w-12 h-6 rounded-full transition ${teamSettings.smsActive ? 'bg-green-500' : 'bg-gray-300'}`}
                          onClick={() => updateTeam({ smsActive: !teamSettings.smsActive })}>
                          <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${teamSettings.smsActive ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                        </div>
                        <div>
                          <span className="font-semibold text-sm flex items-center gap-1"><Phone size={14} /> SMS</span>
                          <span className="text-xs text-gray-500">{teamSettings.smsActive ? 'Active' : 'Inactive - Twilio not configured'}</span>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Recipients */}
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Mail size={20} className="text-brand-blue" /> Designated Recipients - {settingsTeamTab}</h3>
                      <button
                        onClick={() => updateTeam({ recipients: [...teamSettings.recipients, { name: '', email: '', phone: '' }] })}
                        className="flex items-center gap-1 bg-brand-blue text-white px-3 py-2 rounded-md hover:bg-blue-700 transition text-sm font-semibold"
                        data-testid="add-recipient-btn"
                      >
                        <Plus size={16} /> Add Recipient
                      </button>
                    </div>

                    {teamSettings.recipients.map((recipient, idx) => (
                      <div key={idx} className="flex items-center gap-3 mb-3 p-3 bg-gray-50 rounded-lg" data-testid={`recipient-row-${idx}`}>
                        <div className="flex-1">
                          <label className="text-xs font-semibold text-gray-500 mb-1 block">Name</label>
                          <input type="text" value={recipient.name}
                            onChange={(e) => { const u = [...teamSettings.recipients]; u[idx] = { ...u[idx], name: e.target.value }; updateTeam({ recipients: u }); }}
                            placeholder="Full Name" className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none" data-testid={`recipient-name-${idx}`} />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs font-semibold text-gray-500 mb-1 block">Email</label>
                          <input type="email" value={recipient.email}
                            onChange={(e) => { const u = [...teamSettings.recipients]; u[idx] = { ...u[idx], email: e.target.value }; updateTeam({ recipients: u }); }}
                            placeholder="email@example.com" className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none" data-testid={`recipient-email-${idx}`} />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs font-semibold text-gray-500 mb-1 block">Phone (SMS)</label>
                          <input type="tel" value={recipient.phone}
                            onChange={(e) => { const u = [...teamSettings.recipients]; u[idx] = { ...u[idx], phone: e.target.value }; updateTeam({ recipients: u }); }}
                            placeholder="+1 (555) 000-0000" className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none" data-testid={`recipient-phone-${idx}`} />
                        </div>
                        {teamSettings.recipients.length > 1 && (
                          <button onClick={() => updateTeam({ recipients: teamSettings.recipients.filter((_, i) => i !== idx) })}
                            className="mt-5 p-2 text-red-500 hover:bg-red-50 rounded-md transition" data-testid={`remove-recipient-${idx}`}>
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Schedule */}
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4"><Clock size={20} className="text-brand-blue" /> Report Schedule - {settingsTeamTab}</h3>
                    <p className="text-sm text-gray-500 mb-6">Only reports from agents on team {settingsTeamTab} who have saved/submitted will be sent.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="p-4 border-2 border-blue-100 rounded-lg bg-blue-50/30">
                        <h4 className="font-bold text-brand-blue mb-3 text-sm uppercase tracking-wide">Weekly Activity Schedule</h4>
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <label className="text-xs font-semibold text-gray-500 mb-1 block">Day</label>
                            <select value={teamSettings.activitySchedule?.dayOfWeek ?? 0}
                              onChange={(e) => updateTeam({ activitySchedule: { ...teamSettings.activitySchedule, dayOfWeek: parseInt(e.target.value) } })}
                              className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none" data-testid="activity-schedule-day">
                              <option value={0}>Sunday</option><option value={1}>Monday</option><option value={2}>Tuesday</option>
                              <option value={3}>Wednesday</option><option value={4}>Thursday</option><option value={5}>Friday</option><option value={6}>Saturday</option>
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="text-xs font-semibold text-gray-500 mb-1 block">Time</label>
                            <input type="time" value={teamSettings.activitySchedule?.time || '12:00'}
                              onChange={(e) => updateTeam({ activitySchedule: { ...teamSettings.activitySchedule, time: e.target.value } })}
                              className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none" data-testid="activity-schedule-time" />
                          </div>
                        </div>
                      </div>
                      <div className="p-4 border-2 border-orange-100 rounded-lg bg-orange-50/30">
                        <h4 className="font-bold text-brand-orange mb-3 text-sm uppercase tracking-wide">Weekly Goals Schedule</h4>
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <label className="text-xs font-semibold text-gray-500 mb-1 block">Day</label>
                            <select value={teamSettings.goalsSchedule?.dayOfWeek ?? 0}
                              onChange={(e) => updateTeam({ goalsSchedule: { ...teamSettings.goalsSchedule, dayOfWeek: parseInt(e.target.value) } })}
                              className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none" data-testid="goals-schedule-day">
                              <option value={0}>Sunday</option><option value={1}>Monday</option><option value={2}>Tuesday</option>
                              <option value={3}>Wednesday</option><option value={4}>Thursday</option><option value={5}>Friday</option><option value={6}>Saturday</option>
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="text-xs font-semibold text-gray-500 mb-1 block">Time</label>
                            <input type="time" value={teamSettings.goalsSchedule?.time || '12:00'}
                              onChange={(e) => updateTeam({ goalsSchedule: { ...teamSettings.goalsSchedule, time: e.target.value } })}
                              className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none" data-testid="goals-schedule-time" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Save */}
                  <div className="flex justify-end">
                    <button
                      onClick={async () => {
                        setIsSavingSettings(true);
                        try {
                          const token = localStorage.getItem('agentToken');
                          const result = await updateReportSettings({ ...teamSettings, team: settingsTeamTab }, token);
                          if (result.success) toast.success(`Settings for ${settingsTeamTab} saved!`);
                        } catch (error) { toast.error('Failed to save settings'); }
                        finally { setIsSavingSettings(false); }
                      }}
                      disabled={isSavingSettings}
                      className="flex items-center gap-2 bg-brand-blue text-white px-6 py-3 rounded-md hover:bg-blue-700 transition font-semibold disabled:opacity-50"
                      data-testid="save-settings-btn"
                    >
                      <Check size={18} />
                      {isSavingSettings ? 'Saving...' : `Save ${settingsTeamTab} Settings`}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
      </div>
    </AgentLayout>
  );
};

export default AdminReports;
